import {IconBoltFilled} from "@tabler/icons-react";
import React, {useEffect, useMemo, useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";
import useDraggableButtonPosition from "@/hooks/useDraggableButtonPosition";
import {useGameMetadataStore} from "@/store/useGameMetadataStore";
import {useGetTopGames} from "../../hooks/gamesList/useGetTopGames";
import {useAuthStore} from "../../store/useAuth.store";
import {useBingoStore} from "../../store/useBingo.store";
import {useHorsesStore} from "../../store/useHorses.store";
import {useMiniGames} from "../../store/useMiniGames";
import MiniGameModal from "./MiniGameModal";
import styles from "./MultiGameFloatingButton.module.css";
import MultiGameModal from "./MultiGameModal";
import ParallelGameModal from "./ParallelGameModal";

const MultiGameFloatingButton = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMiniModalOpen, setIsMiniModalOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const buttonRef = React.useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({x: 0, y: 0});
  const [buttonPosition, setButtonPosition] = useDraggableButtonPosition();
  const hasDraggedRef = React.useRef(false);
  const dragStartPos = React.useRef({x: 0, y: 0});
  const isTouchActiveRef = React.useRef(false);

  const [parallelGame, setParallelGame] = useState(null);
  const [isParallelGameMinimized, setIsParallelGameMinimized] = useState(false);
  const [isDocumentFullscreen, setIsDocumentFullscreen] = useState(false);

  const {t} = useTranslation();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const {data: topGamesData} = useGetTopGames();
  const initializeGames = useGameMetadataStore(state => state.initializeGames);
  const getGameById = useGameMetadataStore(state => state.getGameById);
  const gamesLoading = useGameMetadataStore(state => state.loading);
  const gamesInitialized = useGameMetadataStore(state => state.initialized);
  const allGames = useGameMetadataStore(state => state.allGames);
  const screenMode = useMiniGames(state => state.screenMode);
  const horsesScreenMode = useHorsesStore(state => state.screenMode);
  const bingoScreenMode = useBingoStore(state => state.screenMode);
  const location = useLocation();

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement ||
        (window.innerHeight === screen.height && window.innerWidth === screen.width)
      );
      setIsDocumentFullscreen(isFullscreen);
    };

    const handleKeyPress = event => {
      if (event.key === "m" || event.key === "M") {
        if (isDocumentFullscreen) {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
          }
          setTimeout(() => {
            setIsModalOpen(true);
          }, 300);
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);
    window.addEventListener("resize", handleFullscreenChange);
    document.addEventListener("keydown", handleKeyPress);

    handleFullscreenChange();

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", handleFullscreenChange);
      window.removeEventListener("resize", handleFullscreenChange);
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [isDocumentFullscreen]);

  useEffect(() => {
    if (!gamesInitialized && !gamesLoading) {
      initializeGames();
    }
  }, [gamesInitialized, gamesLoading, initializeGames]);

  const topSixGames = React.useMemo(() => {
    const listTopGames = topGamesData?.data?.data || [];

    if (!gamesInitialized || !allGames || allGames.length === 0) {
      return [];
    }

    const processedGames = listTopGames.reduce((acc, topGame) => {
      const game = getGameById(topGame.game_id, topGame.game_provider);

      if (game) {
        acc.push(game);
      } else {
        acc.push({
          id: topGame.game_id,
          provider: topGame.game_provider,
          name: topGame.game_name || topGame.name || topGame.game_id,
          game_name: topGame.game_name || topGame.name || topGame.game_id,
          icon: topGame.icon || topGame.game_icon || topGame.image || null,
          isFav: false,
          enable: true,
        });
      }
      return acc;
    }, []);

    return processedGames.slice(0, 6);
  }, [topGamesData, allGames, gamesInitialized, getGameById]);

  useEffect(() => {
    const handleOtherMenuOpen = () => {
      if (isModalOpen) {
        setIsModalOpen(false);
      }
      if (isMiniModalOpen) {
        setIsMiniModalOpen(false);
      }
    };

    window.addEventListener("floating-menu-opened", handleOtherMenuOpen);
    window.addEventListener("wallet-menu-opened", handleOtherMenuOpen);

    return () => {
      window.removeEventListener("floating-menu-opened", handleOtherMenuOpen);
      window.removeEventListener("wallet-menu-opened", handleOtherMenuOpen);
    };
  }, [isModalOpen, isMiniModalOpen]);

  useEffect(() => {
    if (!hasInteracted) {
      setIsModalOpen(false);
      setIsMiniModalOpen(false);
    }
  }, [authStatus, hasInteracted]);

  const handleButtonClick = () => {
    if (hasDraggedRef.current) {
      hasDraggedRef.current = false;
      return;
    }

    if (authStatus !== "authenticated") {
      useAuthStore.getState().handleAuthForm("login");
      handleShowAuthModal();
      return;
    }

    setHasInteracted(true);
    window.dispatchEvent(new CustomEvent("multi-game-menu-opened"));
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsMiniModalOpen(false);
  };

  const handleMinimizeModal = () => {
    setIsModalOpen(false);
    setIsMiniModalOpen(true);
  };

  const handleCloseMiniModal = () => {
    setIsMiniModalOpen(false);
  };

  const handleOpenFullModal = () => {
    setIsMiniModalOpen(false);
    setIsModalOpen(true);
  };

  const handleGameSelect = selectedGame => {
    setIsModalOpen(false);
    setIsMiniModalOpen(false);
    setParallelGame(selectedGame);
    setIsParallelGameMinimized(false);
  };

  const handleCloseParallelGame = () => {
    setParallelGame(null);
    setIsParallelGameMinimized(false);
  };

  const handleMinimizeParallelGame = () => {
    setIsParallelGameMinimized(!isParallelGameMinimized);
  };

  const handleMouseDown = e => {
    e.preventDefault();
    hasDraggedRef.current = false;
    dragStartPos.current = {x: e.clientX, y: e.clientY};
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  };

  const handleMouseMove = e => {
    if (!isDragging) return;
    e.preventDefault();

    const buttonSize = 70;
    const bottomNavHeight = 90;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxY = viewportHeight - buttonSize - bottomNavHeight;

    let newX = e.clientX - dragOffset.x;
    let newY = e.clientY - dragOffset.y;

    if (newX < 0) {
      newX = 0;
      setDragOffset(prev => ({...prev, x: e.clientX}));
    } else if (newX > viewportWidth - buttonSize) {
      newX = viewportWidth - buttonSize;
      setDragOffset(prev => ({...prev, x: e.clientX - newX}));
    }

    if (newY < 0) {
      newY = 0;
      setDragOffset(prev => ({...prev, y: e.clientY}));
    } else if (newY > maxY) {
      newY = maxY;
      setDragOffset(prev => ({...prev, y: e.clientY - newY}));
    }

    setButtonPosition({x: newX, y: newY});

    const dx = e.clientX - dragStartPos.current.x;
    const dy = e.clientY - dragStartPos.current.y;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      hasDraggedRef.current = true;
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      if (buttonPosition) {
        setButtonPosition(buttonPosition);
      }
    }
  };

  const handleTouchStart = e => {
    e.preventDefault();
    hasDraggedRef.current = false;
    isTouchActiveRef.current = true;
    const touch = e.touches[0];
    dragStartPos.current = {x: touch.clientX, y: touch.clientY};
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    });
    setIsDragging(true);
  };

  const handleTouchMove = e => {
    if (!isDragging) return;
    e.preventDefault();

    const touch = e.touches[0];
    const buttonSize = 70;
    const bottomNavHeight = 90;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxY = viewportHeight - buttonSize - bottomNavHeight;

    let newX = touch.clientX - dragOffset.x;
    let newY = touch.clientY - dragOffset.y;

    if (newX < 0) {
      newX = 0;
      setDragOffset(prev => ({...prev, x: touch.clientX}));
    } else if (newX > viewportWidth - buttonSize) {
      newX = viewportWidth - buttonSize;
      setDragOffset(prev => ({...prev, x: touch.clientX - newX}));
    }

    if (newY < 0) {
      newY = 0;
      setDragOffset(prev => ({...prev, y: touch.clientY}));
    } else if (newY > maxY) {
      newY = maxY;
      setDragOffset(prev => ({...prev, y: touch.clientY - newY}));
    }

    setButtonPosition({x: newX, y: newY});

    const dx = touch.clientX - dragStartPos.current.x;
    const dy = touch.clientY - dragStartPos.current.y;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      hasDraggedRef.current = true;
    }
  };

  const handleTouchEnd = () => {
    isTouchActiveRef.current = false;
    setIsDragging(false);

    if (!hasDraggedRef.current) {
      handleButtonClick();
    } else if (buttonPosition) {
      setButtonPosition(buttonPosition);
    }
  };

  useEffect(() => {
    if (!isDragging) return;

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove, {passive: false});
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging]);

  const handlersRef = React.useRef({handleTouchStart, handleTouchEnd});
  handlersRef.current = {handleTouchStart, handleTouchEnd};

  useEffect(() => {
    const btn = buttonRef.current;
    if (!btn) return;

    const onTouchStart = e => handlersRef.current.handleTouchStart(e);
    const onTouchEnd = () => handlersRef.current.handleTouchEnd();

    btn.addEventListener("touchstart", onTouchStart, {passive: false});
    btn.addEventListener("touchend", onTouchEnd);
    return () => {
      btn.removeEventListener("touchstart", onTouchStart);
      btn.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  const isOverlayFullscreen = useMemo(() => {
    const gamesFs = screenMode === "fullscreen";
    const horsesFs = horsesScreenMode === "fullscreen";
    const bingoFs = bingoScreenMode === "fullscreen";
    return Boolean(gamesFs || horsesFs || bingoFs);
  }, [screenMode, horsesScreenMode, bingoScreenMode]);

  const isInAnyGameScreen = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith("/games/")) {
      const parts = p.split("/").filter(Boolean);
      return parts.length >= 3;
    }
    if (p.startsWith("/horses")) return true;
    if (p.startsWith("/bingo")) return true;
    return false;
  }, [location.pathname]);

  const buttonStyle = useMemo(() => {
    const baseStyle = {
      padding: 0,
      border: "none",
      borderRadius: "50%",
      cursor: isDragging ? "grabbing" : "grab",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "56px",
      height: "56px",
      transition: isDragging ? "none" : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    };

    if (buttonPosition) {
      return {
        ...baseStyle,
        position: "fixed",
        top: `${buttonPosition.y}px`,
        left: `${buttonPosition.x}px`,
        zIndex: 99999,
      };
    }

    if (isDocumentFullscreen) {
      return {
        ...baseStyle,
        position: "fixed",
        top: "60%",
        right: "20px",
        zIndex: 99999,
        pointerEvents: "auto",
        visibility: "visible",
        opacity: "1",
        display: "flex",
        isolation: "isolate",
        willChange: "transform",
      };
    } else if (isInAnyGameScreen && isOverlayFullscreen) {
      return {
        ...baseStyle,
        position: "fixed",
        top: "60%",
        right: "20px",
        zIndex: 99999,
      };
    } else {
      return {
        ...baseStyle,
        position: "fixed",
        bottom: "100px",
        right: "15px",
        zIndex: 99999,
      };
    }
  }, [isOverlayFullscreen, isDocumentFullscreen, isInAnyGameScreen, buttonPosition, isDragging]);

  const [portalEl] = useState(() => {
    const el = document.createElement("div");
    el.setAttribute("id", "multi-game-portal");
    return el;
  });

  useEffect(() => {
    if (!document.body.contains(portalEl)) {
      document.body.appendChild(portalEl);
    }
    return () => {
      if (document.body.contains(portalEl)) document.body.removeChild(portalEl);
    };
  }, [portalEl]);

  if (authStatus !== "authenticated") {
    return null;
  }

  if (topSixGames.length === 0) {
    return null;
  }

  if (location.pathname.startsWith("/lotoven")) {
    return null;
  }

  const buttonElement = !isMiniModalOpen && !isModalOpen && (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleButtonClick}
      onMouseDown={handleMouseDown}
      aria-label={t("multiGameFloatingButton")}
      data-testid="multi-game-floating-button"
      id={"button-multi-game"}
      className={`${styles.multiGameButton} ${
        isDocumentFullscreen ? "multi-game-button-fullscreen" : ""
      } ${isDragging ? styles.dragging : ""}`}
      style={buttonStyle}
      onDoubleClick={() => {
        // Double-click to exit fullscreen when in fullscreen mode
        if (isDocumentFullscreen) {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
          }
        }
      }}
    >
      <IconBoltFilled
        size={24}
        className={styles.buttonIcon}
      />
    </button>
  );

  return (
    <>
      {buttonElement && createPortal(buttonElement, portalEl)}

      <MultiGameModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onMinimize={handleMinimizeModal}
        games={topSixGames}
        onGameSelect={handleGameSelect}
      />

      <MiniGameModal
        isOpen={isMiniModalOpen}
        onClose={handleCloseMiniModal}
        onMinimize={handleOpenFullModal}
        games={topSixGames}
        onGameSelect={handleGameSelect}
      />

      {parallelGame && (
        <ParallelGameModal
          game={parallelGame}
          onClose={handleCloseParallelGame}
          onMinimize={handleMinimizeParallelGame}
          isMinimized={isParallelGameMinimized}
        />
      )}
    </>
  );
};

export default MultiGameFloatingButton;
